import { useCallback, useMemo } from 'react';
import { useSettings } from '@/entities/settings';
import { getAncestorGroupIds } from '../lib';
import type { NavigationNode } from './types';

/**
 * Результат {@link useCollapsibleSections}.
 */
export type CollapsibleSections = {
  /** Идентификаторы свёрнутых узлов: их заголовки видны, а содержимое и подсекции — нет. */
  collapsedNodeIds: Set<string>;
  /** Переключает свёрнутость узла (по клику на его заголовок). */
  toggleSection: (nodeId: string) => void;
  /** Разворачивает узел вместе со всеми предками — чтобы его значения стали видны. */
  expandSection: (nodeId: string) => void;
  /** Идентификаторы узлов, отрисованных в «портянке», в document-order. */
  visibleNodeIds: string[];
};

/**
 * Собирает id узлов, чьи заголовки присутствуют в «портянке»: узел виден, пока
 * ни один из его предков не свёрнут (заголовок самого свёрнутого узла остаётся).
 */
const collectVisibleNodeIds = (nodes: NavigationNode[], collapsedNodeIds: Set<string>): string[] =>
  nodes.flatMap((node) => [
    node.id,
    ...(collapsedNodeIds.has(node.id) ? [] : collectVisibleNodeIds(node.children ?? [], collapsedNodeIds)),
  ]);

/**
 * Состояние сворачивания секций «портянки» атрибутов.
 *
 * Свёрнутость — настройка пользователя: она хранится в настройках интерфейса, поэтому
 * переживает переход к другому диалогу и следующий вход в систему. Хранятся только
 * свёрнутые узлы, узел без записи развёрнут. Записи узлов, которых в открытом диалоге
 * нет, не участвуют в отрисовке и не вычищаются — они применяются в диалогах с такими
 * блоками.
 */
export const useCollapsibleSections = (nodes: NavigationNode[]): CollapsibleSections => {
  const { dialogsPage, setDialogsSettings } = useSettings();
  const collapsedNodes = useMemo(() => dialogsPage.attrCollapsedNodes ?? [], [dialogsPage.attrCollapsedNodes]);

  const collapsedNodeIds = useMemo(() => new Set(collapsedNodes), [collapsedNodes]);

  const toggleSection = useCallback(
    (nodeId: string) => {
      const next = collapsedNodeIds.has(nodeId)
        ? collapsedNodes.filter((id) => id !== nodeId)
        : [...collapsedNodes, nodeId];
      setDialogsSettings({ attrCollapsedNodes: next });
    },
    [collapsedNodes, collapsedNodeIds, setDialogsSettings],
  );

  const expandSection = useCallback(
    (nodeId: string) => {
      if (collapsedNodes.length === 0) return;
      const pathIds = new Set([...getAncestorGroupIds(nodes, nodeId), nodeId]);
      const next = collapsedNodes.filter((id) => !pathIds.has(id));
      if (next.length === collapsedNodes.length) return;
      setDialogsSettings({ attrCollapsedNodes: next });
    },
    [collapsedNodes, nodes, setDialogsSettings],
  );

  const visibleNodeIds = useMemo(() => collectVisibleNodeIds(nodes, collapsedNodeIds), [nodes, collapsedNodeIds]);

  return { collapsedNodeIds, toggleSection, expandSection, visibleNodeIds };
};
